import { useTranslation } from 'react-i18next'
import { useState, useEffect, useCallback } from 'react'
import SubVisual from '../../components/SubVisual'

// 개발 관련 이미지들
const developmentWorkspaceImage =
	'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80'
const teamCollaborationImage =
	'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80'
const serverSecurityImage =
	'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=2034&q=80'

const AiIntegrationLab = () => {
	const { t } = useTranslation()
	const [visibleSections, setVisibleSections] = useState<string[]>([])

	const handleIntersection = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const newSections: string[] = []
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const sectionId = entry.target.getAttribute('data-section')
					if (sectionId) {
						newSections.push(sectionId)
					}
				}
			})

			if (newSections.length > 0) {
				setVisibleSections((prev) => {
					const updated = [...prev]
					newSections.forEach((section) => {
						if (!updated.includes(section)) {
							updated.push(section)
						}
					})
					return updated
				})
			}
		},
		[],
	)

	useEffect(() => {
		const observer = new IntersectionObserver(handleIntersection, {
			threshold: 0.15,
			rootMargin: '0px 0px -5% 0px',
		})

		const timeoutId = setTimeout(() => {
			document
				.querySelectorAll('[data-section]')
				.forEach((el) => observer.observe(el))
		}, 100)

		return () => {
			clearTimeout(timeoutId)
			observer.disconnect()
		}
	}, [handleIntersection])

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900/20 to-gray-900 text-white overflow-x-hidden touch-action-manipulation">
			{/* 서브 비주얼 */}
			<SubVisual
				backgroundVideo="/videos/integrationLab.mp4"
				title={t('page.aiIntegrationLab.title')}
				subtitle={t('page.aiIntegrationLab.description')}
				height="h-[60vh]"
				overlayOpacity={0.6}
			/>

			{/* AI 통합개발 섹션 */}
			<section
				data-section="integration-dev"
				className={`relative w-full py-20 px-6 lg:px-12 xl:px-20 transition-all duration-1000 ${
					visibleSections.includes('integration-dev')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				{/* 배경 그라데이션 */}
				<div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 via-blue-600/10 to-cyan-600/10 rounded-3xl blur-3xl"></div>

				<div className="relative container mx-auto grid lg:grid-cols-2 gap-16 items-center">
					{/* 텍스트 영역 */}
					<div className="space-y-8">
						<div className="space-y-6">
							<div
								className={`inline-flex items-center px-4 py-2 bg-indigo-500/20 rounded-full border border-indigo-400/30 transition-all duration-1000 delay-200 ${
									visibleSections.includes('integration-dev')
										? 'opacity-100 translate-x-0'
										: 'opacity-0 -translate-x-8'
								}`}
							>
								<span className="text-indigo-300 text-sm font-medium">
									AI Integration Development
								</span>
							</div>

							<h2
								className={`text-3xl lg:text-5xl font-bold bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent leading-tight transition-all duration-1000 delay-300 ${
									visibleSections.includes('integration-dev')
										? 'opacity-100 translate-x-0'
										: 'opacity-0 -translate-x-8'
								}`}
							>
								{t('page.aiIntegrationLab.txt1')}
							</h2>

							<h3
								className={`text-xl lg:text-2xl text-gray-200 font-light transition-all duration-1000 delay-400 ${
									visibleSections.includes('integration-dev')
										? 'opacity-100 translate-x-0'
										: 'opacity-0 -translate-x-8'
								}`}
							>
								{t('page.aiIntegrationLab.txt2')}{' '}
								<span className="text-cyan-300 font-medium">
									{t('page.aiIntegrationLab.txt3')}
								</span>
							</h3>
						</div>

						<p
							className={`text-base text-gray-300 leading-relaxed transition-all duration-1000 delay-500 ${
								visibleSections.includes('integration-dev')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
							}`}
						>
							{t('page.aiIntegrationLab.txt4')}
						</p>

						<p
							className={`text-sm text-gray-400 leading-relaxed transition-all duration-1000 delay-600 ${
								visibleSections.includes('integration-dev')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
							}`}
						>
							{t('page.aiIntegrationLab.txt5')}
						</p>

						<div
							className={`bg-gradient-to-r from-indigo-500/10 to-cyan-500/10 p-6 rounded-xl border border-indigo-400/20 transition-all duration-1000 delay-700 ${
								visibleSections.includes('integration-dev')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
							}`}
						>
							<div className="flex items-center space-x-3 mb-3">
								<div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
								<span className="text-cyan-300 font-semibold text-sm">
									{t('page.aiIntegrationLab.txt6')}
								</span>
							</div>
							<p className="text-indigo-200 text-sm">
								{t('page.aiIntegrationLab.txt7')}
							</p>
						</div>
					</div>

					{/* 개발 프로세스 시각화 */}
					<div
						className={`relative lg:col-span-1 transition-all duration-1000 delay-400 ${
							visibleSections.includes('integration-dev')
								? 'opacity-100 translate-x-0 scale-100'
								: 'opacity-0 translate-x-8 scale-95'
						}`}
					>
						<div className="relative w-full h-80 bg-gradient-to-br from-indigo-600/20 to-cyan-600/20 rounded-2xl overflow-hidden border border-indigo-400/20">
							<div className="absolute inset-0 bg-gradient-to-br from-transparent via-indigo-500/10 to-cyan-500/20"></div>

							{/* 코딩 터미널 시뮬레이션 */}
							<div className="absolute top-4 left-4 right-4">
								<div className="bg-gray-900/50 rounded-lg p-4 border border-indigo-400/30">
									<div className="flex items-center space-x-2 mb-3">
										<div className="w-3 h-3 bg-red-400 rounded-full"></div>
										<div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
										<div className="w-3 h-3 bg-green-400 rounded-full"></div>
										<span className="text-xs text-gray-400 ml-2">
											AI_Integration_Terminal
										</span>
									</div>

									<div className="space-y-2 text-xs font-mono">
										<div className="text-cyan-400">
											$ npm install @ai-integration/core
										</div>
										<div className="text-green-400">✓ AI modules loaded successfully</div>
										<div className="text-indigo-400">$ deploy --platform production</div>
										<div className="text-yellow-400 animate-pulse">
											⚡ Building AI-powered solution...
										</div>
									</div>
								</div>
							</div>

							{/* AI 연결 노드들 */}
							<div className="absolute bottom-4 left-4 right-4">
								<div className="grid grid-cols-3 gap-2">
									{['API', 'ML', 'DB'].map((tech, idx) => (
										<div
											key={tech}
											className={`bg-indigo-500/20 p-2 rounded-lg text-center border border-indigo-400/30 motion-safe:animate-pulse`}
											style={{ animationDelay: `${idx * 0.5}s` }}
										>
											<span className="text-xs text-indigo-300 font-semibold">{tech}</span>
										</div>
									))}
								</div>
							</div>

							{/* 플로팅 요소들 */}
							<div className="absolute top-6 right-6 w-4 h-4 bg-cyan-500/30 rounded-full blur-sm motion-safe:animate-bounce hidden md:block"></div>
							<div
								className="absolute bottom-8 right-8 w-3 h-3 bg-indigo-500/30 rounded-full blur-sm motion-safe:animate-bounce hidden md:block"
								style={{ animationDelay: '1s' }}
							></div>
						</div>
					</div>
				</div>
			</section>

			{/* 개발 워크스페이스 이미지 섹션 */}
			<section
				data-section="workspace-image"
				className={`relative w-full h-[300px] md:h-[700px] overflow-hidden transition-all duration-1000 ${
					visibleSections.includes('workspace-image') ? 'opacity-100' : 'opacity-0'
				}`}
			>
				<div
					className={`relative w-full h-full group cursor-pointer transition-all duration-1000 ${
						visibleSections.includes('workspace-image') ? 'scale-100' : 'scale-105'
					}`}
				>
					<img
						src={developmentWorkspaceImage}
						alt="AI Development Workspace"
						className="w-full h-full object-cover transition-transform duration-700 hover:scale-110 transform-gpu"
					/>
					<div className="absolute inset-0 bg-gray-900/70 transition-all duration-700 ease-in-out group-hover:bg-gray-900/50"></div>
					<div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-indigo-900/30 to-gray-800/60 transition-all duration-700 ease-in-out group-hover:from-gray-900/70 group-hover:via-indigo-900/20 group-hover:to-gray-800/40"></div>

					<div className="absolute inset-0 flex items-center justify-center">
						<div className="text-center px-8 max-w-4xl transform transition-all duration-700 group-hover:scale-105">
							<div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-500 delay-200 transform translate-y-0 md:translate-y-4 md:group-hover:translate-y-0">
								<div className="bg-gray-900/40 backdrop-blur-sm p-4 md:p-8 rounded-2xl border border-indigo-400/30 group-hover:border-indigo-400/50 transition-all duration-700 ease-in-out">
									<h3 className="text-white text-xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-4 group-hover:text-cyan-200 transition-colors duration-500">
										{t('page.aiIntegrationLab.vs1')}
									</h3>
									<p className="text-gray-200 text-sm md:text-lg leading-relaxed group-hover:text-white transition-colors duration-500">
										{t('page.aiIntegrationLab.vs2')}
										<br className="hidden md:block" />
										{t('page.aiIntegrationLab.vs3')}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* 개발 경험 섹션 */}
			<section
				data-section="dev-experience"
				className={`relative py-20 px-6 lg:px-12 xl:px-20 w-full transition-all duration-1000 ${
					visibleSections.includes('dev-experience')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				<div className="absolute inset-0 bg-gradient-to-r from-cyan-600/10 via-indigo-600/10 to-blue-600/10 rounded-3xl blur-3xl"></div>

				<div className="relative container mx-auto">
					<div className="text-center space-y-6 mb-16">
						<div
							className={`inline-flex items-center px-4 py-2 bg-cyan-500/20 rounded-full border border-cyan-400/30 transition-all duration-1000 delay-200 ${
								visibleSections.includes('dev-experience')
									? 'opacity-100 scale-100'
									: 'opacity-0 scale-95'
							}`}
						>
							<span className="text-cyan-300 text-sm font-medium">
								{t('page.aiIntegrationLab.dp1')}
							</span>
						</div>

						<h2
							className={`text-3xl lg:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-indigo-400 bg-clip-text text-transparent leading-tight transition-all duration-1000 delay-300 ${
								visibleSections.includes('dev-experience')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
							}`}
						>
							{t('page.aiIntegrationLab.dp2')}
						</h2>

						<p
							className={`text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto transition-all duration-1000 delay-400 ${
								visibleSections.includes('dev-experience')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
							}`}
						>
							{t('page.aiIntegrationLab.dp3')}
							<br className="hidden md:block" />
							{t('page.aiIntegrationLab.dp4')}
						</p>
					</div>

					{/* 개발 경험 카드들 */}
					<div
						className={`grid md:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 delay-600 ${
							visibleSections.includes('dev-experience')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-8'
						}`}
					>
						{[
							{
								title: t('page.aiIntegrationLab.dp5'),
								desc: t('page.aiIntegrationLab.dp6'),
								image:
									'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
								color:
									'from-cyan-500/10 to-blue-500/10 border-cyan-400/20 hover:border-cyan-400/40',
								titleColor: 'text-cyan-300',
							},
							{
								title: t('page.aiIntegrationLab.dp7'),
								desc: t('page.aiIntegrationLab.dp8'),
								image:
									'https://images.unsplash.com/photo-1516321497487-e288fb19713f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
								color:
									'from-indigo-500/10 to-purple-500/10 border-indigo-400/20 hover:border-indigo-400/40',
								titleColor: 'text-indigo-300',
							},
							{
								title: t('page.aiIntegrationLab.dp9'),
								desc: t('page.aiIntegrationLab.dp10'),
								image:
									'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80',
								color:
									'from-blue-500/10 to-cyan-500/10 border-blue-400/20 hover:border-blue-400/40',
								titleColor: 'text-blue-300',
							},
							{
								title: t('page.aiIntegrationLab.dp11'),
								desc: t('page.aiIntegrationLab.dp12'),
								image:
									'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80',
								color:
									'from-purple-500/10 to-indigo-500/10 border-purple-400/20 hover:border-purple-400/40',
								titleColor: 'text-purple-300',
							},
						].map((project, idx) => (
							<div
								key={idx}
								className={`bg-gradient-to-br ${project.color} p-6 rounded-2xl border transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl min-h-[200px] flex flex-col`}
							>
								<div className="w-full h-32 mb-4 rounded-lg overflow-hidden">
									<img
										src={project.image}
										alt={project.title}
										className="w-full h-full object-cover"
									/>
								</div>
								<h4
									className={`text-lg font-bold mb-3 text-center ${project.titleColor}`}
								>
									{project.title}
								</h4>
								<p className="text-gray-300 text-sm leading-relaxed flex-1 text-center">
									{project.desc}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* 팀 협업 이미지 섹션 */}
			<section
				data-section="team-collaboration"
				className={`relative w-full h-[300px] md:h-[700px] overflow-hidden transition-all duration-1000 ${
					visibleSections.includes('team-collaboration')
						? 'opacity-100'
						: 'opacity-0'
				}`}
			>
				<div
					className={`relative w-full h-full group cursor-pointer transition-all duration-1000 ${
						visibleSections.includes('team-collaboration') ? 'scale-100' : 'scale-105'
					}`}
				>
					<img
						src={teamCollaborationImage}
						alt="Team Collaboration in Development"
						className="w-full h-full object-cover transition-transform duration-700 hover:scale-110 transform-gpu"
					/>
					<div className="absolute inset-0 bg-gray-900/70 transition-all duration-700 ease-in-out group-hover:bg-gray-900/50"></div>
					<div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-cyan-900/25 to-gray-800/60 transition-all duration-700 ease-in-out group-hover:from-gray-900/70 group-hover:via-cyan-900/15 group-hover:to-gray-800/40"></div>

					<div className="absolute inset-0 flex items-center justify-center">
						<div className="text-center px-8 max-w-4xl transform transition-all duration-700 group-hover:scale-105">
							<div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-500 delay-200 transform translate-y-0 md:translate-y-4 md:group-hover:translate-y-0">
								<div className="bg-gray-900/40 backdrop-blur-sm p-4 md:p-8 rounded-2xl border border-cyan-400/30 group-hover:border-cyan-400/60 transition-all duration-700 ease-in-out">
									<h3 className="text-white text-xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-4 group-hover:text-cyan-200 transition-colors duration-500">
										<span className="text-cyan-300 group-hover:text-cyan-100 transition-colors duration-500">
											{t('page.aiIntegrationLab.cw1')}
										</span>
									</h3>
									<p className="text-gray-200 text-sm md:text-lg leading-relaxed group-hover:text-white transition-colors duration-500">
										{t('page.aiIntegrationLab.cw2')}
										<br className="hidden md:block" />
										{t('page.aiIntegrationLab.cw3')}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* 비즈니스 보안 솔루션 섹션 */}
			<section
				data-section="security-solution"
				className={`relative py-20 px-6 lg:px-12 xl:px-20 w-full transition-all duration-1000 ${
					visibleSections.includes('security-solution')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				<div className="absolute inset-0 bg-gradient-to-r from-violet-600/10 via-indigo-600/10 to-blue-600/10 rounded-3xl blur-3xl"></div>

				<div className="relative container mx-auto grid lg:grid-cols-2 gap-16 items-center">
					{/* 보안 시각화 */}
					<div
						className={`relative lg:col-span-1 transition-all duration-1000 delay-400 ${
							visibleSections.includes('security-solution')
								? 'opacity-100 -translate-x-0 scale-100'
								: 'opacity-0 -translate-x-8 scale-95'
						}`}
					>
						<div className="relative w-full h-80 bg-gradient-to-br from-violet-600/20 to-indigo-600/20 rounded-2xl overflow-hidden border border-violet-400/20">
							<div className="absolute inset-0 bg-gradient-to-br from-transparent via-violet-500/10 to-indigo-500/20"></div>

							{/* 보안 방패 시각화 */}
							<div className="absolute inset-0 flex items-center justify-center">
								<div className="relative">
									<div className="w-32 h-32 bg-gradient-to-br from-violet-500/30 to-indigo-500/30 rounded-full flex items-center justify-center border-2 border-violet-400/40">
										<span className="text-4xl">🛡️</span>
									</div>

									{/* 보안 레이어들 */}
									<div className="absolute -inset-4 border-2 border-violet-400/20 rounded-full animate-ping"></div>
									<div className="absolute -inset-8 border border-indigo-400/20 rounded-full animate-pulse"></div>
								</div>
							</div>

							{/* 보안 요소들 */}
							<div className="absolute top-4 left-4 bg-violet-500/20 px-3 py-1 rounded-full border border-violet-400/30">
								<span className="text-xs text-violet-300 font-semibold">
									{t('page.aiIntegrationLab.biz10')}
								</span>
							</div>
							<div className="absolute top-4 right-4 bg-indigo-500/20 px-3 py-1 rounded-full border border-indigo-400/30">
								<span className="text-xs text-indigo-300 font-semibold">
									{t('page.aiIntegrationLab.biz11')}
								</span>
							</div>
							<div className="absolute bottom-4 left-4 bg-blue-500/20 px-3 py-1 rounded-full border border-blue-400/30">
								<span className="text-xs text-blue-300 font-semibold">
									{t('page.aiIntegrationLab.biz12')}
								</span>
							</div>
							<div className="absolute bottom-4 right-4 bg-purple-500/20 px-3 py-1 rounded-full border border-purple-400/30">
								<span className="text-xs text-purple-300 font-semibold">
									{t('page.aiIntegrationLab.biz13')}
								</span>
							</div>
						</div>
					</div>

					{/* 텍스트 영역 */}
					<div className="lg:col-span-1 space-y-8">
						<div className="space-y-6">
							<div
								className={`inline-flex items-center px-4 py-2 bg-violet-500/20 rounded-full border border-violet-400/30 transition-all duration-1000 delay-200 ${
									visibleSections.includes('security-solution')
										? 'opacity-100 translate-x-0'
										: 'opacity-0 translate-x-8'
								}`}
							>
								<span className="text-violet-300 text-sm font-medium">
									{t('page.aiIntegrationLab.biz1')}
								</span>
							</div>

							<h2
								className={`text-3xl lg:text-5xl font-bold bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent leading-tight transition-all duration-1000 delay-300 ${
									visibleSections.includes('security-solution')
										? 'opacity-100 translate-x-0'
										: 'opacity-0 translate-x-8'
								}`}
							>
								{t('page.aiIntegrationLab.biz2')}
							</h2>

							<h3
								className={`text-xl lg:text-2xl text-gray-200 font-light transition-all duration-1000 delay-400 ${
									visibleSections.includes('security-solution')
										? 'opacity-100 translate-x-0'
										: 'opacity-0 translate-x-8'
								}`}
							>
								<span className="text-violet-300 font-medium">
									{t('page.aiIntegrationLab.biz3')}
								</span>
							</h3>
						</div>

						<p
							className={`text-base text-gray-300 leading-relaxed transition-all duration-1000 delay-500 ${
								visibleSections.includes('security-solution')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
							}`}
						>
							{t('page.aiIntegrationLab.biz4')}
						</p>

						<div
							className={`space-y-4 transition-all duration-1000 delay-600 ${
								visibleSections.includes('security-solution')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
							}`}
						>
							{[
								t('page.aiIntegrationLab.biz5'),
								t('page.aiIntegrationLab.biz6'),
								t('page.aiIntegrationLab.biz7'),
								t('page.aiIntegrationLab.biz8'),
							].map((feature, idx) => (
								<div
									key={idx}
									className="flex items-center space-x-3"
								>
									<div className="w-2 h-2 bg-violet-400 rounded-full"></div>
									<span className="text-gray-300">{feature}</span>
								</div>
							))}
						</div>

						<div
							className={`bg-gradient-to-r from-violet-500/10 to-indigo-500/10 p-6 rounded-xl border border-violet-400/20 transition-all duration-1000 delay-700 ${
								visibleSections.includes('security-solution')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
							}`}
						>
							<p className="text-violet-200 font-medium text-sm">
								{t('page.aiIntegrationLab.biz9')}
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* 서버 보안 이미지 섹션 */}
			<section
				data-section="server-security"
				className={`relative w-full h-[300px] md:h-[700px] overflow-hidden transition-all duration-1000 ${
					visibleSections.includes('server-security') ? 'opacity-100' : 'opacity-0'
				}`}
			>
				<div
					className={`relative w-full h-full group cursor-pointer transition-all duration-1000 ${
						visibleSections.includes('server-security') ? 'scale-100' : 'scale-105'
					}`}
				>
					<img
						src={serverSecurityImage}
						alt="Advanced Server Security Infrastructure"
						className="w-full h-full object-cover transition-transform duration-700 hover:scale-110 transform-gpu"
					/>
					<div className="absolute inset-0 bg-gray-900/70 transition-all duration-700 ease-in-out group-hover:bg-gray-900/50"></div>
					<div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-violet-900/25 to-gray-900/90 transition-all duration-700 ease-in-out group-hover:from-gray-900/70 group-hover:via-violet-900/15 group-hover:to-gray-900/70"></div>

					<div className="absolute inset-0 flex items-center justify-center">
						<div className="text-center px-8 max-w-4xl transform transition-all duration-700 group-hover:scale-105">
							<div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-500 delay-200 transform translate-y-0 md:translate-y-4 md:group-hover:translate-y-0">
								<div className="bg-gray-900/40 backdrop-blur-sm p-4 md:p-8 rounded-2xl border border-violet-400/30 group-hover:border-violet-400/60 transition-all duration-700 ease-in-out">
									<h3 className="text-white text-xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4 bg-gradient-to-r from-violet-300 to-indigo-300 bg-clip-text text-transparent group-hover:from-violet-200 group-hover:to-indigo-200 transition-all duration-500">
										{t('page.aiIntegrationLab.inf1')}
									</h3>
									<p className="text-gray-200 text-sm md:text-xl leading-relaxed group-hover:text-white transition-colors duration-500">
										{t('page.aiIntegrationLab.inf2')}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* CTA 섹션 */}
			<section
				data-section="cta"
				className={`relative py-20 px-6 lg:px-12 xl:px-20 w-full text-center transition-all duration-1000 ${
					visibleSections.includes('cta')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				<div className="bg-gradient-to-r container mx-auto from-indigo-600/20 via-violet-600/20 to-cyan-600/20 p-12 rounded-3xl border border-indigo-400/20">
					<h3
						className={`text-3xl font-bold text-white mb-4 transition-all duration-700 delay-200 ${
							visibleSections.includes('cta')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-4'
						}`}
					>
						{t('page.aiIntegrationLab.req1')}
					</h3>
					<p
						className={`text-gray-300 mb-8 text-lg transition-all duration-700 delay-300 ${
							visibleSections.includes('cta')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-4'
						}`}
					>
						{t('page.aiIntegrationLab.req2')}
					</p>
					<div
						className={`space-y-4 md:space-y-0 md:space-x-6 md:flex md:items-center md:justify-center transition-all duration-700 delay-400 ${
							visibleSections.includes('cta')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-4'
						}`}
					>
						<a
							className={`bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:transform hover:scale-105 shadow-lg delay-400 ${
								visibleSections.includes('cta')
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
							}`}
							href="mailto:contact@mindecline.com"
							target="_blank"
							rel="noreferrer"
						>
							${t('page.aiIntegrationLab.req3')}
						</a>
					</div>
				</div>
			</section>
		</div>
	)
}

export default AiIntegrationLab
